import React, { useState, useEffect, useRef } from 'react'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { iframeResizer } from 'iframe-resizer'
import { Events as ScrollEvents } from 'react-scroll'

import useRefCallback from 'hooks/useRefCallback'
import { isScrolledIntoView } from 'lib/dom'

import {
  parseURL,
  appendQueryString,
  classString,
  isBrowser,
  isHTMLDocument,
  APP_URL,
} from 'helpers'

const EVENT_INTERACTED = 'form-interacted'
const EVENT_LOADED = 'form-loaded'
const EVENT_SUBMITTED = 'form-submitted'

const StyledIframe = styled.iframe`
  width: 100%;
  border: none;
  padding: 0;
  margin: 0;
`

const EmbedForm = ({
  src,
  name,
  minHeight,
  title,
  loading,
  className,
  innerRef,
  onFormViewed,
  onFormLoaded,
  onFormInteracted,
  onFormSubmitted,
  style: propsStyle,
  ...others
}) => {
  let iframeRef = useRef(null)
  const [computedSRC, setComputedSRC] = useState(null)
  let hasSentFormViewedEvent = false
  let handleScroll = null
  let handleMessage = null

  // This is confusing, it's true. We want to grab the query params from the
  // rendering page and pass them through to the embedded form. It's
  // complicated because of server side rendering: the component is rendered
  // when the site is built. At that time, we obviously dont have a window or
  // the url that this component is rendered on. We need a URL to grab the
  // query params from, so we render this component empty, get the URL, then
  // render the embedded form with the params.
  function getSRCWithUTMParams () {
    const query = isBrowser() // gatsby issues with server rendering, so this
      ? parseURL(get(window, 'location.search') || '').query
      : {}

    return appendQueryString(src, {
      ...query,
      // We need this `ref` param for tracking which URL was used to submit
      // a contact form.
      ref: window.location.href,
    })
  }

  function focusFirst () {
    if (iframeRef) {
      // this is a thing from the iframe-resizer package
      iframeRef?.contentWindow?.postMessage('focus', '*')
    }
  }

  useEffect(() => {
    if (name) {
      ScrollEvents.scrollEvent.register('end', (to, element) => {
        if (to === name) focusFirst()
      })
    }

    setComputedSRC(getSRCWithUTMParams())
  }, [src])

  useEffect(() => {
    iframeResizer(
      {
        autoResize: true,
        checkOrigin: [
          'http://localhost:3000',
          'https://app.useanvil.com',
          'https://staging.useanvil.com',
        ],
      },
      '.embed-form'
    )
  }, [computedSRC])

  const [tourRefCallback] = useRefCallback({
    onNodeMount: (node) => {
      handleScroll = () => {
        if (
          !hasSentFormViewedEvent &&
          isScrolledIntoView({ element: node }) &&
          isBrowser() &&
          isHTMLDocument() &&
          onFormViewed
        ) {
          onFormViewed()
          hasSentFormViewedEvent = true
        }
      }
      // run the scroll function on mount, in case the form is already in view
      handleScroll()

      handleMessage = function (event) {
        if (
          event.origin === APP_URL &&
          event.source === node.contentWindow &&
          isBrowser() &&
          isHTMLDocument()
        ) {
          if (onFormLoaded && event?.data?.action === EVENT_LOADED) {
            onFormLoaded()
          }

          if (onFormInteracted && event?.data?.action === EVENT_INTERACTED) {
            onFormInteracted()
          }

          if (onFormSubmitted && event?.data?.action === EVENT_SUBMITTED) {
            onFormSubmitted()
          }
        }
      }

      addEventListener('scroll', handleScroll)
      addEventListener('message', handleMessage)
    },
    onNodeExit: (node) => {
      removeEventListener('scroll', handleScroll)
      removeEventListener('message', handleMessage)
    },
  })

  const style = { minHeight, ...propsStyle }

  if (!computedSRC) {
    return <div style={style} {...others} />
  }

  return (
    <StyledIframe
      className={classString(['embed-form', className])}
      style={style}
      {...others}
      ref={(iframe) => {
        iframeRef = iframe
        tourRefCallback(iframe)
        // TODO: check by ref type, need to support funcs and objects
        // if (typeof innerRef === 'function')

        if (innerRef) innerRef(iframe)
      }}
      title={title}
      loading={loading}
      src={computedSRC} // NOTE: must come AFTER others, since we compute the src. if before others, the props will override this
    />
  )
}

EmbedForm.defaultProps = {
  loading: 'eager',
}

EmbedForm.propTypes = {
  name: PropTypes.string, // the scroll name, will setup a scroll watcher to focus the form
  src: PropTypes.string.isRequired,
  minHeight: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  loading: PropTypes.oneOf(['eager', 'lazy']),
  injectedStyles: PropTypes.string,
  className: PropTypes.string,
  innerRef: PropTypes.func,
}

export default React.forwardRef((props, ref) => {
  return <EmbedForm {...props} innerRef={ref} />
})
